import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import {
  Crosshair,
  Zap,
  Shield,
  ChevronDown,
  SunMoon,
  Radio,
  Signal,
  LogIn,
  LogOut,
  User as UserIcon,
  Target,
  Flame,
  Axe,
  Mountain,
  Wrench,
  Trees,
  Pickaxe,
  Star,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { useGame } from "../context/GameContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../context/FavoritesContext";
import { useLatency } from "../context/LatencyContext";
import { formatNumber } from "../lib/format";
import { Link, useLocation } from "react-router-dom";

const ICONS = { Crosshair, Zap, Shield, Target, Flame, Axe, Mountain, Wrench, Trees, Pickaxe };

const GameIcon = ({ name, className, style }) => {
  const C = ICONS[name] || Crosshair;
  return <C className={className} style={style} />;
};

const TopNav = ({ onOpenFavorites }) => {
  const { games, game, setGameId, byGroup } = useGame();
  const { themeId, toggle } = useTheme();
  const { user, loading, login, logout } = useAuth();
  const { favorites } = useFavorites();
  const { baseline, enabled: probeEnabled, toggle: toggleProbe, remeasure } = useLatency();
  const location = useLocation();
  const activeGameId = game?.id || "wardogs";
  const onTrends = location.pathname.startsWith("/trends");
  const onRankings = location.pathname.startsWith("/rankings");

  const totalOnlinePlayers = games.reduce((s, g) => s + (g.total_players || 0), 0);
  const totalServers = games.reduce((s, g) => s + (g.total_servers || 0), 0);

  return (
    <header
      className="scanlines sticky top-0 z-40 border-b"
      style={{
        borderColor: "var(--border-color)",
        background: "color-mix(in srgb, var(--bg-main) 85%, transparent)",
        backdropFilter: "blur(10px)",
      }}
    >
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
        {/* Logo */}
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <img
            src="/server-recon-logo.webp"
            alt="Server-Recon"
            className="w-10 h-10 object-contain rise"
            style={{ filter: "drop-shadow(0 0 6px rgba(0, 255, 150, 0.35))" }}
          />
          <div className="leading-tight">
            <div className="font-head text-xl font-extrabold tracking-wider" style={{ color: "var(--text-primary)" }}>
              SERVER<span style={{ color: "var(--primary-accent)" }}>-</span>RECON
            </div>
            <div className="hud-caption hidden sm:block">DISCOVER EVERY GAME SERVER / v3.0</div>
          </div>
        </Link>

        {/* Game selector */}
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="game-selector"
                className="font-mono-hud uppercase tracking-widest"
                style={{
                  background: "var(--bg-elevated)",
                  borderColor: "var(--border-strong)",
                  color: "var(--text-primary)",
                  borderRadius: 2,
                }}
              >
                <GameIcon name={game?.icon} className="w-4 h-4 mr-2" style={{ color: "var(--primary-accent)" }} />
                <span className="truncate max-w-[200px]">{game?.name || "Select game"}</span>
                <ChevronDown className="w-4 h-4 ml-2 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="min-w-[320px] max-h-[70vh] overflow-y-auto font-body"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", borderRadius: 2 }}
            >
              {Object.entries(byGroup).map(([groupName, list]) => (
                <React.Fragment key={groupName}>
                  <DropdownMenuLabel className="hud-caption" style={{ color: "var(--primary-accent)" }}>
                    {groupName}
                  </DropdownMenuLabel>
                  {list.map((g) => (
                    <DropdownMenuItem
                      key={g.id}
                      data-testid={`game-option-${g.id}`}
                      onClick={() => setGameId(g.id)}
                      className="cursor-pointer flex items-center gap-3 py-2"
                    >
                      <GameIcon
                        name={g.icon}
                        className="w-4 h-4"
                        style={{ color: g.id === game?.id ? "var(--primary-accent)" : "var(--text-secondary)" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate" style={{ color: "var(--text-primary)" }}>{g.name}</div>
                        <div className="text-xs font-mono-hud" style={{ color: "var(--text-dim)" }}>
                          {g.online_servers}/{g.total_servers} online · {formatNumber(g.total_players)} players
                        </div>
                      </div>
                      {g.id === game?.id && <span className="pulse-dot" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator style={{ background: "var(--border-color)" }} />
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Ticker (hide on small screens) */}
        <div className="hidden xl:flex items-center gap-6 ml-4 flex-1">
          <TickerItem icon={<Radio className="w-3.5 h-3.5" />} label="TOTAL" value={formatNumber(totalServers)} />
          <TickerItem icon={<Signal className="w-3.5 h-3.5" />} label="PLAYERS" value={formatNumber(totalOnlinePlayers)} highlight />
          <TickerItem icon={<Crosshair className="w-3.5 h-3.5" />} label="GAMES" value={games.length} />
        </div>

        <div className="flex-1 xl:hidden" />

        {/* Trends + Rankings nav */}
        <Link to={`/trends/${activeGameId}`} data-testid="nav-trends">
          <Button
            variant="outline"
            className="font-mono-hud text-xs tracking-widest"
            style={{
              background: onTrends ? "var(--primary-accent)" : "var(--bg-elevated)",
              borderColor: onTrends ? "var(--primary-accent)" : "var(--border-strong)",
              color: onTrends ? "#000" : "var(--text-primary)",
              borderRadius: 2,
            }}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            TRENDS
          </Button>
        </Link>
        <Link to={`/rankings/${activeGameId}`} data-testid="nav-rankings">
          <Button
            variant="outline"
            className="font-mono-hud text-xs tracking-widest"
            style={{
              background: onRankings ? "var(--secondary-accent)" : "var(--bg-elevated)",
              borderColor: onRankings ? "var(--secondary-accent)" : "var(--border-strong)",
              color: onRankings ? "#000" : "var(--text-primary)",
              borderRadius: 2,
            }}
          >
            <Trophy className="w-4 h-4 mr-2" />
            TOP 50
          </Button>
        </Link>

        {/* Favorites */}
        <Button
          variant="outline"
          data-testid="favorites-btn"
          onClick={onOpenFavorites}
          className="font-mono-hud text-xs tracking-widest relative"
          style={{
            background: "var(--bg-elevated)",
            borderColor: "var(--border-strong)",
            color: "var(--text-primary)",
            borderRadius: 2,
          }}
        >
          <Star className="w-4 h-4 mr-2" style={{ color: "var(--highlight)" }} />
          FAVORITES
          {favorites.length > 0 && (
            <span
              className="ml-2 px-1.5 py-0.5 text-[10px] font-bold tabular-nums"
              style={{ background: "var(--highlight)", color: "#000", borderRadius: 2 }}
              data-testid="favorites-count"
            >
              {favorites.length}
            </span>
          )}
        </Button>

        {/* Latency probe status */}
        <Button
          variant="outline"
          data-testid="probe-toggle"
          onClick={toggleProbe}
          onDoubleClick={remeasure}
          title={probeEnabled ? "Probe-adjusted pings (double-click to re-measure)" : "Showing regional estimates"}
          className="font-mono-hud text-xs tracking-widest"
          style={{
            background: "var(--bg-elevated)",
            borderColor: probeEnabled ? "var(--primary-accent)" : "var(--border-strong)",
            color: probeEnabled ? "var(--primary-accent)" : "var(--text-secondary)",
            borderRadius: 2,
          }}
        >
          <Signal className="w-4 h-4 mr-2" />
          {baseline == null ? "…" : `${baseline}MS`}
        </Button>

        {/* Theme toggle */}
        <Button
          variant="outline"
          data-testid="theme-toggle"
          onClick={toggle}
          className="font-mono-hud text-xs tracking-widest"
          style={{
            background: "var(--bg-elevated)",
            borderColor: "var(--border-strong)",
            color: "var(--primary-accent)",
            borderRadius: 2,
          }}
        >
          <SunMoon className="w-4 h-4 mr-2" />
          {themeId === "military" ? "TACTICAL" : "NEON"}
        </Button>

        {/* Auth */}
        {!loading && (user && user.user_id) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="user-menu"
                className="font-mono-hud text-xs tracking-widest flex items-center gap-2"
                style={{
                  background: "var(--bg-elevated)",
                  borderColor: "var(--primary-accent)",
                  color: "var(--text-primary)",
                  borderRadius: 2,
                }}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <UserIcon className="w-4 h-4" />
                )}
                <span className="truncate max-w-[120px]">{user.name || user.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", borderRadius: 2 }}
            >
              <DropdownMenuLabel className="hud-caption">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator style={{ background: "var(--border-color)" }} />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          !loading && (
            <Button
              onClick={login}
              data-testid="login-btn"
              className="font-mono-hud text-xs tracking-widest font-bold"
              style={{
                background: "var(--primary-accent)",
                color: "#000",
                borderRadius: 2,
              }}
            >
              <LogIn className="w-4 h-4 mr-2" />
              LOGIN
            </Button>
          )
        )}
      </div>
    </header>
  );
};

const TickerItem = ({ icon, label, value, highlight }) => (
  <div className="flex items-center gap-2 border-l pl-4" style={{ borderColor: "var(--border-color)" }}>
    <span style={{ color: "var(--primary-accent)" }}>{icon}</span>
    <div>
      <div className="hud-label">{label}</div>
      <div
        className="font-mono-hud text-base font-bold tabular-nums"
        style={{ color: highlight ? "var(--highlight)" : "var(--text-primary)" }}
      >
        {value}
      </div>
    </div>
  </div>
);

export default TopNav;
